<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :column-defs="columnDefs"
            :row-data="rowData"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            grid-theme="ag-theme-alpine-report"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            />
            <div class="d-none d-sm-flex align-items-baseline mr-1 mb-1">
                <properties-search-multiselect
                    v-model="filterProperties"
                    variant="primary"
                    :properties="filterPropertyOptions"
                />
            </div>
            <div v-if="showFilterCleaner" class="d-none d-sm-flex align-items-baseline mr-1 mb-1">
                <cleaners-search-multiselect
                    v-model="filterCleaners"
                    variant="primary"
                    :cleaners="filterCleanerOptions"
                />
            </div>
            <export-to-excel-button
                :href="exportExcelHref"
            ></export-to-excel-button>
            <exportToPDFButton
                :href="exportPDFHref"
                :pdfCacheKey="pdfCacheKey"
            />
           
            <b-button-group class="mr-1">
                <more-filters-dropdown class="housekeeping-dropdown">
                    <template
                        v-slot:default="{
                            handleDropdownShow,
                            handleDropdownHide,
                        }"
                    >
                        <properties-search-multiselect
                            class="d-sm-none d-flex"
                            v-model="filterProperties"
                            variant="primary"
                            :properties="filterPropertyOptions"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                        <cleaners-search-multiselect
                            v-if="showFilterCleaner"
                            class="d-sm-none d-flex"
                            v-model="filterCleaners"
                            variant="primary"
                            :cleaners="filterCleanerOptions"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                        <form-square-switch
                            v-for="filter in housekeepingFilters"
                            :key="filter.key"
                            :labels="{
                                checked: filter.labels,
                                unchecked: filter.labels,
                            }"
                            v-model="filterValues[filter.key]"
                            class="mb-1 housekeeping-toggle"
                            :sync="true"
                        />
                    </template>
                </more-filters-dropdown>
            </b-button-group>
            <responsive-button
                variant="outline-primary"
                @click="generate"
                :responsiveness="false"
                >Generate</responsive-button
            >
            <report-settings-modal
                :initialValues="reportSettingsValues"
                :modalId="reportSettingsModalId"
            />
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import { format, isEqual, isAfter } from 'date-fns';
import API from '../../../api';
import Page from '../../generic/page/page.vue';
import Grid from '../../generic/grid/grid.vue';
import CleanersSearchMultiselect from '../../generic/cleaners-search-multiselect/cleaners-search-multiselect.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import PropertiesSearchMultiselect from '../../generic/properties-search-multiselect/properties-search-multiselect.vue';
import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
import ExportToPDFButton from '../../generic/export-to-pdf-button/export-to-pdf-button.vue';
import ExportToExcelButton from '../../generic/export-to-excel-button/export-to-excel-button.vue';
import ReportSummaryBar from '../report-summary-bar.vue';
import ReportSettingsModal, {
    DATE_PERIODS_INPUT,
    MULTI_SELECT_INPUT,
    SWITCH_INPUT,
    PROPERTIES_MULTI_SELECT,
} from '../report-settings/report-settings-modal.vue';
import ArrivalInfoCellRenderer from './arrival-info-cell-renderer.vue';
import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
import { isMediumScreen } from '../../../utils/screens';
import {
    REPORT_HOUSEKEEPING_CLEANERS_ENDPOINT,
    REPORT_HOUSEKEEPING_ENDPOINT,
    REPORT_HOUSEKEEPING_PROPERTIES_ENDPOINT,
} from '../../../endpoints';
import DateRangePicker, {
    nameToRange,
    LAST_N_DAYS_30,
    LAST_N_DAYS_14,
    LAST_N_DAYS_7,
    YESTERDAY,
    TODAY,
    TOMORROW,
    NEXT_N_DAYS_7,
    NEXT_N_DAYS_14,
    NEXT_N_DAYS_30,
    LAST_YEAR,
    THIS_YEAR,
} from '../../generic/date-range-picker/date-range-picker.vue';

export default {
    name: 'HousekeepingReport',
    components: {
        Page,
        Grid,
        ResponsiveButton,
        DateRangePicker,
        reportSummaryBar: ReportSummaryBar,
        ExportToPDFButton,
        ExportToExcelButton,
        FormSquareSwitch,
        MoreFiltersDropdown,
        CleanersSearchMultiselect,
        PropertiesSearchMultiselect,
        ReportSettingsModal,
        arrivalInfoCellRenderer: ArrivalInfoCellRenderer,
    },
    mixins: [removeScrollMixin],
    data() {
        const defaultShowBlocks =
            this.$store.getters['housekeepingLocalStorage/showBlocks'];
        const defaultShowSameDayTurnoversOnly =
            this.$store.getters[
                'housekeepingLocalStorage/showSameDayTurnoversOnly'
            ];
        const defaultShowDisabledProperties =
            this.$store.getters[
                'housekeepingLocalStorage/showDisabledProperties'
            ];
        return {
            title: 'Housekeeping',
            tabs: [
                {
                    title: 'Housekeeping',
                    route: '/app/reports/housekeeping/',
                    active: true,
                },
            ],
            dateRange: nameToRange(NEXT_N_DAYS_30),
            predefinedRanges: {
                collapsed: [
                    TODAY,
                    TOMORROW,
                    NEXT_N_DAYS_7,
                    NEXT_N_DAYS_14,
                    NEXT_N_DAYS_30,
                ],
                expanded: [
                    TODAY,
                    TOMORROW,
                    NEXT_N_DAYS_7,
                    NEXT_N_DAYS_14,
                    NEXT_N_DAYS_30,
                    LAST_YEAR,
                    LAST_N_DAYS_7,
                    LAST_N_DAYS_14,
                    LAST_N_DAYS_30,
                    YESTERDAY,
                    THIS_YEAR
                ]
            },
            dateRequestFormat: 'yyyy-MM-dd',
            busy: false,
            gridApi: null,
            rowData: [],
            filterPropertyOptions: [],
            filterPropertyOptionIds: new Set(),
            filterCleanerOptions: [],
            filterCleanerOptionIds: new Set(),
            filterValues: {
                showBlocks: defaultShowBlocks,
                showSameDayTurnoversOnly: defaultShowSameDayTurnoversOnly,
                showDisabledProperties: defaultShowDisabledProperties,
            },
            statusBar: null,
            pdfCacheKey: null,
            reportSettingsModalId: 'report-settings-modal-id',
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        propertiesRequest() {
            return this.filterProperties
                .map((property) => property.property_id)
                .join(',');
        },
        cleanersRequest() {
            return this.filterCleaners.join(',');
        },
        showFilterCleaner() {
            return this.filterCleanerOptions?.length;
        },
        reportSettingsValues() {
            return {
                name: 'Housekeeping Settings',
                report_sysname: 'HOUSEKEEPING',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'next_30_days',
                        label: 'Date Range',
                        name: 'date_range',
                        predefinedRanges: [
                            LAST_YEAR,
                            LAST_N_DAYS_30,
                            LAST_N_DAYS_14,
                            LAST_N_DAYS_7,
                            YESTERDAY,
                            TODAY,
                            TOMORROW,
                            NEXT_N_DAYS_7,
                            NEXT_N_DAYS_14,
                            NEXT_N_DAYS_30,
                            THIS_YEAR,
                        ],
                    },
                    {
                        type: PROPERTIES_MULTI_SELECT,
                        options: this.filterPropertyOptions,
                        name: 'properties',
                        value: [],
                        label: 'Only For Properties',
                        placeholder: 'All Properties'
                    },
                    {
                        type: MULTI_SELECT_INPUT,
                        options: this.filterCleanerOptions.map((item) => {
                            return {
                                value: item.id,
                                text: item.full_name,
                            };
                        }),
                        label: 'Cleaners',
                        name: 'cleaners',
                        value: [],
                        placeholder: 'All Cleaners'
                    },
                    {
                        type: SWITCH_INPUT,
                        label: 'Show Owner/iCal Blocks',
                        name: 'show_blocks',
                        value: false,
                    },
                    {
                        type: SWITCH_INPUT,
                        label: 'Show Same Day Turnovers Only',
                        name: 'show_same_day_turnovers_only',
                        value: false,
                    },
                    {
                        type: SWITCH_INPUT,
                        label: 'Show Disabled Properties',
                        name: 'show_disabled_properties',
                        value: false,
                    },
                ],
            };
        },
        filterProperties: {
            get() {
                const defaultProperties =
                    this.$store.getters['housekeepingLocalStorage/properties'];
                return defaultProperties.filter((property) =>
                    this.filterPropertyOptionIds.has(property.property_id)
                );
            },
            set(properties) {
                this.$store.dispatch(
                    'housekeepingLocalStorage/changeProperties',
                    properties
                );
            },
        },
        filterCleaners: {
            get() {
                const defaultCleaners =
                    this.$store.getters['housekeepingLocalStorage/cleaners'];
                return defaultCleaners.filter((cleanerId) =>
                    this.filterCleanerOptionIds.has(cleanerId)
                );
            },
            set(cleaners) {
                this.$store.dispatch(
                    'housekeepingLocalStorage/changeCleaners',
                    cleaners
                );
            },
        },
        columnDefs() {
            return [
                {
                    field: 'guest',
                    headerName: 'Departure Info',
                    autoHeight: true,
                    pinned: 'left',
                    width: isMediumScreen() ? undefined : 200,
                    autoWidthExclude: this.isMobile,
                    cellStyle: { lineHeight: '1.2rem', padding: '0.8rem' },
                    cellRenderer: ({ data }) => {
                        let template = `
                            <div>
                            <div class="housekeeping-lead-text">${data.guest}</div>
                            <div>${data.to_date_time_str}</div>
                        `;
                        if (!!data.clean_on_date_str) {
                            template += `
                                <div>
                                    Clean on
                                    <span ${!!data.clean_on_date_is_custom ? 'class="text-danger"' : ''}>
                                        ${data.clean_on_date_str}
                                    </span>
                                </div>
                            `;
                        }
                        if (!!data.has_pets && !!data.number_of_pets) {
                            template += `
                                <div>
                                    Pets: ${data.number_of_pets}
                                </div>
                            `;
                        }
                        template += '</div>';
                        return template;
                    },
                    comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
                        const toDateA = new Date(nodeA.data.to_date_time);
                        const cleanOnA = !!nodeA.data.clean_on_date ? new Date(nodeA.data.clean_on_date) : null;
                        const toDateB = new Date(nodeB.data.to_date_time);
                        const cleanOnB = !!nodeB.data.clean_on_date ? new Date(nodeB.data.clean_on_date) : null;
                        if (!!cleanOnA && !!cleanOnB) {
                            if (isEqual(cleanOnA, cleanOnB)) {
                                if (isEqual(toDateA, toDateB)) return 0;
                                return isAfter(toDateA, toDateB) ? 1 : -1;
                            }
                            return isAfter(cleanOnA, cleanOnB) ? 1 : -1;
                        } else if (!!cleanOnA && !cleanOnB) {
                            if (isEqual(cleanOnA, toDateB)) return 0;
                            return isAfter(cleanOnA, toDateB) ? 1 : -1;
                        } else if (!cleanOnA && !!cleanOnB) {
                            if (isEqual(toDateA, cleanOnB)) return 0;
                            return isAfter(toDateA, cleanOnB) ? 1 : -1;
                        }
                        if (isEqual(toDateA, toDateB)) return 0;
                        return isAfter(toDateA, toDateB) ? 1 : -1;
                    },
                },
                {
                    field: 'rental_property_name',
                    headerName: 'Property',
                },
                {
                    field: 'house_keeper_full_names',
                    headerName: 'Cleaner',
                    cellRenderer: ({ data }) => {
                        return `<span class="housekeeping-lead-text">${data.house_keeper_full_names}</span>`;
                    },
                },
                {
                    field: 'arrival_info',
                    headerName: 'Arrival Info',
                    autoHeight: true,
                    sortable: false,
                    cellRenderer: 'arrivalInfoCellRenderer',
                },
                {
                    field: '',
                    headerName: '',
                    autoWidthExclude: true,
                    sortable: false,
                    pdfExportOptions: { skipColumn: true },
                },
            ];
        },
        exportExcelHref() {
            let url = '/app/reports/housekeeping/row-data/excel/';
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&show_blocks=${this.filterValues.showBlocks}`;
            url += `&show_disabled_properties=${this.filterValues.showDisabledProperties}`;
            url += `&show_same_day_turnovers_only=${this.filterValues.showSameDayTurnoversOnly}`;
            url += `&properties=${this.propertiesRequest}`;
            url += `&cleaners=${this.cleanersRequest}`;
            return url;
        },
        exportPDFHref() {
            let url = '/app/reports/housekeeping/row-data/pdf/';
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&show_blocks=${this.filterValues.showBlocks}`;
            url += `&show_disabled_properties=${this.filterValues.showDisabledProperties}`;
            url += `&show_same_day_turnovers_only=${this.filterValues.showSameDayTurnoversOnly}`;
            url += `&properties=${this.propertiesRequest}`;
            url += `&cleaners=${this.cleanersRequest}`;
            return url;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        housekeepingFilters() {
            return [
                {
                    labels: 'Show Owner/iCal Blocks',
                    key: 'showBlocks',
                },
                {
                    labels: 'Show Same Day Turnovers Only',
                    key: 'showSameDayTurnoversOnly',
                },
                {
                    labels: 'Show Disabled Properties',
                    key: 'showDisabledProperties',
                },
            ];
        },
        isMobile() {
            const md = 768;
            return window.innerWidth < md || window.innerHeight < md;
        },
    },
    mounted() {
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.onResize();
        window.addEventListener('resize', this.onResize);
        Promise.all([this.fetchProperties(), this.fetchCleaners()]);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    beforeMount() {
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    methods: {
        generate() {
            this.fetchData();
        },
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(REPORT_HOUSEKEEPING_ENDPOINT(), {
                    params: {
                        from_date: this.startDateRequest,
                        to_date: this.endDateRequest,
                        show_blocks: this.filterValues.showBlocks,
                        show_disabled_properties:
                            this.filterValues.showDisabledProperties,
                        show_same_day_turnovers_only:
                            this.filterValues.showSameDayTurnoversOnly,
                        properties: this.propertiesRequest,
                        cleaners: this.cleanersRequest,
                    },
                });
                this.rowData = data.items;
                this.pdfCacheKey = data.pdf_cache_key;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data.' });
            } finally {
                this.busy = false;
            }
        },
        async fetchProperties() {
            try {
                const { data } = await API.get(
                    REPORT_HOUSEKEEPING_PROPERTIES_ENDPOINT()
                );
                this.filterPropertyOptions = data;
                this.filterPropertyOptionIds = new Set(
                    data.map((option) => option.property_id)
                );
            } catch (e) {
                this.errorToast({ text: 'Could not fetch properties.' });
            }
        },
        async fetchCleaners() {
            try {
                const { data } = await API.get(
                    REPORT_HOUSEKEEPING_CLEANERS_ENDPOINT()
                );
                this.filterCleanerOptions = data;
                this.filterCleanerOptionIds = new Set(
                    data.map((option) => option.id)
                );
            } catch (e) {
                this.errorToast({ text: 'Could not fetch cleaners.' });
            }
        },
        onResize() {
            const statusBarComponent =
                this.gridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance =
                    statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
        },
    },
    watch: {
        'filterValues.showBlocks': function (value, previousValue) {
            this.$store.dispatch(
                'housekeepingLocalStorage/changeShowBlocks',
                value
            );
        },
        'filterValues.showSameDayTurnoversOnly': function (
            value,
            previousValue
        ) {
            this.$store.dispatch(
                'housekeepingLocalStorage/changeShowSameDayTurnoversOnly',
                value
            );
        },
        'filterValues.showDisabledProperties': function (value, previousValue) {
            this.$store.dispatch(
                'housekeepingLocalStorage/changeShowDisabledProperties',
                value
            );
        },
    },
};
</script>

<style lang="scss">
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.housekeeping-dropdown {
    :deep(.buttons-wrapper) {
        width: 100%;
    }

    .housekeeping-toggle {
        width: 243px;
        @include media-breakpoint-down(lg) {
            width: 276px;
        }
    }
}

.housekeeping-lead-text {
    font-size: 1rem;
    font-weight: 300;
}
</style>
